import { ZoomableGroup, ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { FetchNumberOfSightseeingPosts } from '../Sightseeing/SightseeingRequests';
import { FetchNumberOfFoodPosts } from '../Food/FoodRequests';
import axios from 'axios';
import { CountrySummary } from './CountryUtility';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface GeoFeature {
  rsmKey: string;
  properties: {
    name: string;
  };
}

interface InteractiveMapProps {
  isMapLoading: boolean;
  isMobile: boolean;
  geoJson: string;
  setSelectedIso: (value: string) => void;
  setSelectedCountry: (value: CountrySummary) => void;
  setIsModalOpen: (value: boolean) => void;
  setFlagUrl: (value: string) => void;
  setCountryNumSightseeingPosts: (value: number | null) => void;
  setCountryNumFoodPosts: (value: number | null) => void;
}

export function InteractiveMap({ isMapLoading, isMobile, geoJson, setSelectedIso, setSelectedCountry, setIsModalOpen, setFlagUrl, setCountryNumSightseeingPosts, setCountryNumFoodPosts }: InteractiveMapProps): JSX.Element {
  // Function to fetch country details
  const handleClick = async (geo: GeoFeature): Promise<void> => {
    try {
      const { data } = await axios.get<CountrySummary>(`${backendUrl}/worlddata/countries/${geo.properties.name}`);
      setIsModalOpen(true);
      setSelectedCountry(data);
      setSelectedIso(data.iso);
      setFlagUrl(`https://flagcdn.com/w320/${data.iso.toLowerCase()}.png`);

      const [numSightseeing, numFood] = await Promise.all([FetchNumberOfSightseeingPosts(data.iso).catch(() => 0), FetchNumberOfFoodPosts(data.iso).catch(() => 0)]);

      setCountryNumSightseeingPosts(numSightseeing);
      setCountryNumFoodPosts(numFood);
    } catch (err) {
      console.error('Error fetching country data', err);
      setCountryNumFoodPosts(null);
      setCountryNumSightseeingPosts(null);
    }
  };

  return isMapLoading ? (
    <div className="loader" />
  ) : (
    <ComposableMap>
      <ZoomableGroup
        center={isMobile ? [10, 40] : [0, 0]}
        zoom={isMobile ? 6 : 1} // Zoom in on mobile
        minZoom={1}
        maxZoom={50}
        translateExtent={[
          [0, -50], // Minimum X (-1000), Minimum Y (-500)
          [900, 700], // Maximum X (1000), Maximum Y (500)
        ]} // Restrict panning
      >
        <Geographies geography={geoJson}>
          {({ geographies }: { geographies: GeoFeature[] }) => {
            return geographies.map((geo) => (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                onClick={() => handleClick(geo)}
                style={{
                  default: {
                    fill: '#f4f4f4',
                    outline: 'none',
                    stroke: '#bbbbbb',
                    strokeWidth: 0.2,
                  },
                  hover: {
                    fill: '#2b6589',
                    outline: 'none', // Remove outline
                    stroke: '#FFFFFF', // Add a white stroke to outline the country
                    strokeWidth: 0.2, // Set the width of the stroke (outline)
                    zIndex: 1000,
                  },
                  pressed: {
                    fill: '#22557f',
                    outline: 'none',
                    stroke: '#FFFFFF', // Add a white stroke when pressed
                    strokeWidth: 0.2, // Set the width of the pressed stroke
                  },
                }}
              />
            ));
          }}
        </Geographies>
      </ZoomableGroup>
    </ComposableMap>
  );
}

export default InteractiveMap;
